"use client"

/**
 * Single-biomarker landing-page section library (AHD-3795).
 *
 * Reusable, biomarker-agnostic sections driven by one `BiomarkerLandingConfig`,
 * so each new add-on LP (Lp(a), then #2/#3) is a config file, not a rebuild.
 * Best-of-both of `/go/female-hormones` (conversion) and `/report` (rich cards
 * with icon chips, mono category badges and colour accents). Layouts vary
 * section to section (full-bleed hero, stat-led panel, rich card grids,
 * scroll-animated stepper, interactive explorer + self-check).
 *
 * Two interactive sections live in `biomarker-landing-interactive.tsx`.
 */
import Image from "next/image"
import Link from "next/link"
import { useTranslations } from "@/lib/intl/use-translations"
import {
  BiomarkerReportMarquee,
  type MarqueeBiomarkerCard,
} from "@/components/ui/biomarker-report-marquee"
import { BookingCta, PRODUCT_IDS } from "@/components/ui/booking-cta"
import { ActionBadge } from "@/components/ui/action-badge"
import {
  ImpactAreaIcon,
  SmartphoneIcon,
  ActionPlanIcon,
  GreenCheck,
  InfoTimeIcon,
  InfoRadiationIcon,
  InfoContrastIcon,
  InfoHealthAreasIcon,
  InfoPreparationIcon,
  InfoForWhomIcon,
} from "@/components/ui/icons"
import { ReviewedByPhysiciansIcon } from "@/components/ui/reviewed-by-physicians-icon"
import {
  Dna,
  EyeSlash,
  CheckCircle,
  Heartbeat,
  Brain,
  HeartHalf,
  ArrowsClockwise,
  ShieldCheck,
  ClipboardText,
  Stethoscope,
  UsersThree,
  WarningCircle,
  ImageSquare,
  ChartLine,
  Moon,
  Drop,
  ArrowsOut,
  Fire,
  Pulse,
} from "@phosphor-icons/react"

/* ── Config type (one object drives every section) ─────────────────────────── */

export type AccentKey = "blue" | "olive" | "terracotta" | "sand" | "aubergine"
export type IconKey =
  | "dna"
  | "eyeSlash"
  | "check"
  | "heart"
  | "brain"
  | "valve"
  | "refresh"
  | "shield"
  | "checklist"
  | "stethoscope"
  | "users"
  | "warning"
  | "actionPlan"
  | "chart"
  | "moon"
  | "drop"
  | "enlarge"
  | "inflammation"
  | "spike"
export type ToneKey = "good" | "warn" | "bad"
export type DetailInfoIconKey =
  | "healthAreas"
  | "time"
  | "radiation"
  | "contrast"
  | "preparation"
  | "forWhom"
export type ImageRef = { src: string; alt: string; position?: string }

export type LandingCard = {
  title: string
  body: string
  badge?: string
  icon?: IconKey
  accent?: AccentKey
  image?: ImageRef
}

export type ExplorerBand = {
  label: string
  tone: ToneKey
  from: number
  to: number
  status: string
  meaning: string
  /** Intensity (1–3) applied to each affected factor bar in this band. */
  level: number
}

export type SelfCheckResult = {
  minChecks: number
  tone: ToneKey
  title: string
  body: string
}

export type BiomarkerLandingConfig = {
  slug: string
  advancedBloodTestHref: string
  hero: {
    eyebrow: string
    /** Short blue "action badge" label (e.g. "Add-on"), rendered as a pill. */
    badge: string
    /**
     * Muted caption under the primary CTA making the add-on dependency
     * explicit ("Only available with the Advanced blood test"). Links to
     * `ctaNoteHref` when set.
     */
    ctaNote?: string
    ctaNoteHref?: string
    heading: string
    body: string
    primaryCta: string
    secondaryCta?: string
    secondaryHref?: string
    rating?: { value: string; label: string }
    image: ImageRef
    /**
     * Fully formatted, locale-correct "from" price beside the primary CTA,
     * currency included — resolved server-side from the Stripe feed via
     * `fetchPricesFromSupabase` + `PRICE_KEY_FOR_SLUG`. Undefined when the
     * market has no authoritative price for this product, in which case the
     * price line is hidden rather than showing a fabricated or mislabelled
     * figure (same rule as `buildFallback` in lib/pricing.ts).
     */
    priceFrom?: string
    /** Floating result cards overlaid on the hero image (test-detail style). */
    floatingCards?: {
      name: string
      value: string
      status: string
      tone: ToneKey
    }[]
  }
  /** Test-detail info card (mirrors the /tests/<slug> "quick facts" block):
   *  benefits checklist + CTAs on the left, tinted info table on the right. */
  detailCard?: {
    heading: string
    benefits: string[]
    /** Primary CTA books the Advanced Blood Test add-on flow (label only). */
    primaryCta: string
    secondaryCta?: { label: string; href: string }
    rows: { icon: DetailInfoIconKey; label: string; value: string }[]
  }
  facts: {
    eyebrow: string
    heading: string
    headlineStat: { value: string; caption: string }
    items: LandingCard[]
  }
  gap: {
    eyebrow: string
    heading: string
    body: string
    image: ImageRef
    /** When set, the section shows the copy on the left with the auto-scrolling
     *  biomarker marquee on the right, instead of the full-bleed image. */
    marquee?: MarqueeBiomarkerCard[]
  }
  explorer: {
    eyebrow: string
    heading: string
    caption: string
    unit: string
    sliderMax: number
    defaultValue: number
    bands: ExplorerBand[]
    /** Specific health aspects whose intensity changes with the slider. */
    factorsLabel: string
    factors: string[]
  }
  risks: {
    eyebrow: string
    heading: string
    body: string
    seeAllLabel: string
    seeAllHref: string
    items: LandingCard[]
  }
  selfCheck: {
    eyebrow: string
    heading: string
    intro: string
    questions: { id: string; label: string }[]
    results: SelfCheckResult[]
    primaryCta: string
  }
  action: {
    eyebrow: string
    heading: string
    body: string
    items: LandingCard[]
    image: ImageRef
  }
  personas: {
    eyebrow: string
    heading: string
    note: string
    items: { name: string; scenario: string; photo: ImageRef }[]
  }
  biomarkerPanel: {
    eyebrow: string
    heading: string
    subheading: string
    ctaLabel: string
  }
  steps: {
    eyebrow: string
    heading: string
    body: string
    items: LandingCard[]
    /** Word before the step number in the walkthrough pill ("Step 01"). */
    stepWord?: string
    /** Outlined CTA under the final step, linking to the example report. */
    sampleReportCta?: string
    /** Outlined CTA under the middle step(s), linking to the locations page. */
    locationsCta?: string
  }
  faq: { heading: string; items: { q: string; a: string }[] }
  closing: {
    eyebrow: string
    heading: string
    body: string
    primaryCta: string
    secondaryCta?: string
    secondaryHref?: string
    image: ImageRef
  }
}

/* ── Shared tokens ─────────────────────────────────────────────────────────── */

export const SECTION = "mx-auto max-w-[1440px] px-6 md:px-10"
export const TONE_FILL: Record<ToneKey, string> = {
  good: "#408c49",
  warn: "#c8b59d",
  bad: "#ed9062",
}
const ICONS: Record<
  IconKey,
  React.ComponentType<{ size?: number; className?: string }>
> = {
  dna: Dna,
  eyeSlash: EyeSlash,
  check: CheckCircle,
  heart: Heartbeat,
  brain: Brain,
  valve: HeartHalf,
  refresh: ArrowsClockwise,
  shield: ShieldCheck,
  checklist: ClipboardText,
  stethoscope: Stethoscope,
  users: UsersThree,
  warning: WarningCircle,
  actionPlan: ActionPlanIcon,
  chart: ChartLine,
  moon: Moon,
  drop: Drop,
  enlarge: ArrowsOut,
  inflammation: Fire,
  spike: Pulse,
}

// Primary CTA — aubergine, used everywhere a booking action is the primary.
export const CTA_PRIMARY =
  "bg-aubergine-800 hover:bg-aubergine-900 type-title-sm inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 font-medium text-white transition-colors"

/** All add-on CTAs book the Advanced Blood Test and deep-link to the add-on
 *  step, where the single biomarker is selected (AHD-3795). */
export function AddonBookingCta({
  children,
  location,
  className = CTA_PRIMARY,
}: {
  children: React.ReactNode
  location: string
  className?: string
}) {
  return (
    <BookingCta
      productId={PRODUCT_IDS.bloodTest}
      step="addons"
      trackingLabel={`${location}_cta`}
      trackingLocation={location}
      className={className}
    >
      {children}
    </BookingCta>
  )
}

// White cards are flat on this page — no border, no shadow (per design direction).
export const CARD_DEPTH = ""

/* ── Eyebrow — plain DS label (sentence case, no rule), per house typography ── */

export function Eyebrow({
  children,
  onDark = false,
}: {
  children: React.ReactNode
  onDark?: boolean
}) {
  return (
    <p
      className={`type-label-md ${onDark ? "text-white/60" : "text-neutral-400"}`}
    >
      {children}
    </p>
  )
}

/** Render body copy with the first occurrence of `phrase` as an underlined link. */
export function LinkedCopy({
  text,
  phrase,
  href,
}: {
  text: string
  phrase: string
  href: string
}) {
  const i = text.indexOf(phrase)
  if (i === -1) return <>{text}</>
  return (
    <>
      {text.slice(0, i)}
      <Link
        href={href}
        className="underline decoration-neutral-300 underline-offset-2 hover:decoration-neutral-400"
      >
        {phrase}
      </Link>
      {text.slice(i + phrase.length)}
    </>
  )
}

/* ── Reusable rich card (report-style: icon chip + mono badge + accent) ─────── */

function FeatureCard({ card }: { card: LandingCard }) {
  const Icon = card.icon ? ICONS[card.icon] : null
  return (
    <div className="flex h-full flex-col items-start gap-4 rounded-[20px] bg-white p-8 text-left">
      {Icon ? <Icon size={32} className="text-neutral-900" /> : null}
      <h3 className="type-title-lg tracking-tight text-neutral-900">
        {card.title}
      </h3>
      <p className="type-body-md text-pretty text-neutral-500">{card.body}</p>
    </div>
  )
}

/* ── 1. Hero — test-detail treatment ───────────────────────────────────────── */
/* Matches the /tests/<slug> hero: mobile = full-bleed image with copy overlaid;
 * desktop = light two-column (copy + inline price + trust row on the left, square
 * image with floating result cards on the right). */

/** Floating result card overlaid on the hero image (test-detail style). */
function HeroResultCard({
  card,
  className,
}: {
  card: NonNullable<BiomarkerLandingConfig["hero"]["floatingCards"]>[number]
  className: string
}) {
  // Translucent pill tinted by tone (alpha overlay — matches test-detail hero).
  const pill =
    card.tone === "good"
      ? "bg-[rgba(184,199,101,0.52)]" // ds-color-ignore: translucent overlay (alpha)
      : card.tone === "warn"
        ? "bg-[rgba(200,181,157,0.55)]" // ds-color-ignore: translucent overlay (alpha)
        : "bg-[rgba(237,144,98,0.5)]" // ds-color-ignore: translucent overlay (alpha)
  return (
    <div
      className={`absolute z-10 max-w-[220px] rounded-[12px] bg-white/70 p-[18px] backdrop-blur-[10px] ${className}`}
    >
      <p className="type-body-lg font-medium text-black">{card.name}</p>
      <span className="type-title-lg mt-0.5 block font-medium text-black">
        {card.value}
      </span>
      <span
        className={`type-label-md mt-1 inline-block rounded-[37px] px-[9px] py-[5px] font-medium text-neutral-900 ${pill}`}
      >
        {card.status}
      </span>
    </div>
  )
}

export function BiomarkerHeroSection({
  config,
}: {
  config: BiomarkerLandingConfig
}) {
  const { hero } = config
  const pt = useTranslations("pricing")
  const tt = useTranslations("tests")
  const trust = [
    {
      Icon: ReviewedByPhysiciansIcon,
      size: 28,
      label: pt("trustReviewedByPhysicians"),
    },
    { Icon: ImpactAreaIcon, size: 28, label: pt("trustPersonalActionPlan") },
    { Icon: SmartphoneIcon, size: 24, label: pt("trustInteractiveReport") },
  ]
  const cards = hero.floatingCards ?? []

  return (
    <>
      {/* ── Mobile: full-bleed background image ── */}
      <section className="relative -mb-px h-lvh min-h-[600px] overflow-hidden md:hidden">
        <Image
          src={hero.image.src}
          alt={hero.image.alt}
          fill
          priority
          sizes="100vw"
          className="object-cover"
          style={{ objectPosition: hero.image.position ?? "center" }}
        />
        <div className="absolute inset-0 z-[1] bg-neutral-950/50" />
        <div className="absolute inset-0 z-[1] bg-gradient-to-b from-transparent via-transparent to-black/30" />
        <div className="absolute inset-0 z-[2] flex flex-col justify-end px-6 pt-[120px] pb-12">
          <div className="mb-4">
            <ActionBadge label={hero.badge} color="light-blue" />
          </div>
          <h1 className="type-display-lg break-words text-white">
            {hero.heading}
          </h1>
          <p className="type-body-xl mt-4 text-white/85">{hero.body}</p>
          <div className="mt-8 flex flex-col items-center gap-4">
            <AddonBookingCta
              location={`go/${config.slug}/hero-mobile`}
              className="type-body-lg text-aubergine-900 inline-flex w-full items-center justify-center rounded-full bg-white px-5 py-3.5 font-medium transition-opacity hover:opacity-90"
            >
              {hero.primaryCta}
            </AddonBookingCta>
            {hero.priceFrom && (
              <div className="flex items-baseline gap-1">
                <span className="text-sm text-white/60">{tt("from")}</span>
                <span className="text-2xl font-semibold text-white">
                  {hero.priceFrom}
                </span>
              </div>
            )}
            {hero.ctaNote &&
              (hero.ctaNoteHref ? (
                <Link
                  href={hero.ctaNoteHref}
                  className="type-body-sm text-center text-white/70 underline decoration-white/30 underline-offset-4 hover:text-white"
                >
                  {hero.ctaNote}
                </Link>
              ) : (
                <p className="type-body-sm text-center text-white/70">
                  {hero.ctaNote}
                </p>
              ))}
          </div>
          <div className="mt-8 grid grid-cols-3 items-start gap-2">
            {trust.map(({ Icon, label }) => (
              <div
                key={label}
                className="flex min-w-0 flex-col items-center gap-1"
              >
                <Icon size={24} className="text-white/70" />
                <p className="text-center text-xs font-medium whitespace-pre-line text-white/70">
                  {label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Desktop: light two-column grid ── */}
      <div className="relative -mt-[120px] hidden bg-white pt-[120px] md:block">
        {/* Subtle top wash behind the navbar (matches /tests hero). */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 z-[1] h-[260px] bg-gradient-to-b from-neutral-100 to-transparent"
          aria-hidden
        />
        <section className="relative z-[2] pt-16 pb-16">
          <div className="mx-auto grid max-w-[1440px] grid-cols-2 items-center gap-8 px-4 md:px-10">
            {/* Left — copy, CTA + price, trust row */}
            <div className="min-w-0">
              <ActionBadge label={hero.badge} color="light-blue" />
              {/* ARIA heading (not native <h1>) so the mobile hero's <h1> stays
                  the single h1 — mirrors the /tests detail hero. */}
              <p
                className="type-display-lg mt-4 break-words text-neutral-900"
                role="heading"
                aria-level={1}
              >
                {hero.heading}
              </p>
              <p className="type-body-xl mt-4 max-w-lg text-neutral-900">
                {hero.body}
              </p>
              <div className="mt-6 flex items-center gap-4">
                <AddonBookingCta location={`go/${config.slug}/hero`}>
                  {hero.primaryCta}
                </AddonBookingCta>
                {hero.priceFrom && (
                  <div className="flex items-baseline gap-1">
                    <span className="text-sm leading-5 text-neutral-900">
                      {tt("from")}
                    </span>
                    <span className="text-2xl leading-8 font-semibold text-neutral-900">
                      {hero.priceFrom}
                    </span>
                  </div>
                )}
              </div>
              {hero.ctaNote &&
                (hero.ctaNoteHref ? (
                  <Link
                    href={hero.ctaNoteHref}
                    className="type-body-sm mt-3 inline-block text-neutral-500 underline decoration-neutral-300 underline-offset-4 hover:text-neutral-700"
                  >
                    {hero.ctaNote}
                  </Link>
                ) : (
                  <p className="type-body-sm mt-3 text-neutral-500">
                    {hero.ctaNote}
                  </p>
                ))}
              <div className="mt-8 flex items-start gap-4">
                {trust.map(({ Icon, size, label }) => (
                  <div
                    key={label}
                    className="flex w-[130px] flex-col items-center gap-1"
                  >
                    <div className="flex size-10 items-center justify-center">
                      <Icon size={size} className="text-neutral-500" />
                    </div>
                    <p className="type-title-sm text-center text-xs leading-4 font-medium whitespace-pre-line text-neutral-900">
                      {label}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — square image with floating result cards */}
            <div className="relative">
              <div className="relative aspect-square w-full overflow-hidden rounded-[12px]">
                <Image
                  src={hero.image.src}
                  alt={hero.image.alt}
                  fill
                  priority
                  sizes="700px"
                  className="object-cover"
                  style={{ objectPosition: hero.image.position ?? "center" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-black/10" />
                {cards[0] && (
                  <HeroResultCard
                    card={cards[0]}
                    className="bottom-4 left-4 hidden lg:block"
                  />
                )}
                {cards[1] && (
                  <HeroResultCard
                    card={cards[1]}
                    className="top-4 right-4 hidden lg:block"
                  />
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

/* ── 1b. Test-detail info card — mirrors the /tests/<slug> quick-facts block ── */
/* Benefits checklist + CTAs on the left; a tinted info table (icon + label +
 * value per row) on the right. Reuses the same Info* icons and surface styling
 * as the shared tests template so a biomarker LP reads as a first-class test. */

const DETAIL_INFO_ICONS: Record<
  DetailInfoIconKey,
  (props: { size?: number; className?: string }) => React.JSX.Element
> = {
  healthAreas: InfoHealthAreasIcon,
  time: InfoTimeIcon,
  radiation: InfoRadiationIcon,
  contrast: InfoContrastIcon,
  preparation: InfoPreparationIcon,
  forWhom: InfoForWhomIcon,
}

export function BiomarkerDetailCardSection({
  config,
}: {
  config: BiomarkerLandingConfig
}) {
  const d = config.detailCard
  if (!d) return null
  return (
    <section className="py-16 md:py-20">
      <div className={SECTION}>
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1fr_1.4fr] lg:items-stretch lg:gap-20">
          {/* Left rail — heading + benefits checklist + CTAs */}
          <div className="flex flex-col justify-between">
            <div>
              <h2 className="type-display-md text-neutral-900">{d.heading}</h2>
              <ul className="mt-8 flex flex-col gap-4">
                {d.benefits.map((benefit) => (
                  <li key={benefit} className="flex items-center gap-3">
                    <GreenCheck width={20} height={20} className="shrink-0" />
                    <span className="type-body-lg text-neutral-700">
                      {benefit}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-8 flex flex-col flex-wrap gap-3 sm:flex-row">
              <AddonBookingCta location={`${config.slug}_detail_card`}>
                {d.primaryCta}
              </AddonBookingCta>
              {d.secondaryCta && (
                <Link
                  href={d.secondaryCta.href}
                  className="type-title-sm inline-flex items-center justify-center gap-2 rounded-full border border-neutral-300 px-7 py-3.5 font-medium text-neutral-900 transition-colors hover:bg-neutral-100"
                >
                  {d.secondaryCta.label}
                </Link>
              )}
            </div>
          </div>
          {/* Right — tinted info table (one row per fact) */}
          <div className="rounded-[16px] bg-neutral-100 px-6 py-6 md:px-10 md:py-8">
            <ul className="flex flex-col gap-2 sm:grid sm:grid-cols-[auto_1fr] sm:items-start sm:gap-x-6 sm:gap-y-6">
              {d.rows.map((row) => {
                const Icon = DETAIL_INFO_ICONS[row.icon]
                return (
                  <li
                    key={row.label}
                    className="grid grid-cols-1 gap-1 py-3 sm:contents"
                  >
                    <div className="flex items-center gap-4">
                      <Icon size={20} className="shrink-0 text-neutral-900" />
                      <span className="type-body-lg text-neutral-900">
                        {row.label}
                      </span>
                    </div>
                    <span className="type-body-lg text-left text-neutral-600">
                      {row.value}
                    </span>
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ── 2. Key facts — stat-led panel + rich card grid ────────────────────────── */

export function BiomarkerFactsSection({
  config,
}: {
  config: BiomarkerLandingConfig
}) {
  const { facts } = config
  return (
    <section id="what-it-is" className="scroll-mt-24 py-12 md:py-16">
      <div
        className={`${SECTION} grid gap-6 md:grid-cols-[0.85fr_1.15fr] md:gap-8`}
      >
        <div className="bg-aubergine-800 relative flex flex-col justify-between gap-10 overflow-hidden rounded-[24px] p-10 text-white">
          <div
            className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full opacity-30 blur-2xl"
            style={{
              background: "radial-gradient(circle,#9958a1,transparent 70%)",
            }}
            aria-hidden
          />
          <div className="relative">
            <Eyebrow onDark>{facts.eyebrow}</Eyebrow>
            <h2 className="type-display-sm mt-4 tracking-tight text-balance text-white">
              {facts.heading}
            </h2>
          </div>
          <div className="relative">
            <p className="type-display-lg text-bright-green-300 leading-none tracking-tight">
              {facts.headlineStat.value}
            </p>
            <p className="type-body-lg mt-3 max-w-[26ch] text-pretty text-white/75">
              {facts.headlineStat.caption}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {facts.items.map((item) => (
            <FeatureCard key={item.title} card={item} />
          ))}
        </div>
      </div>
    </section>
  )
}

/* ── 3. The gap — full-bleed image with copy overlaid ──────────────────────── */

export function BiomarkerGapSection({
  config,
}: {
  config: BiomarkerLandingConfig
}) {
  const { gap } = config

  // Marquee treatment (reused from /report "See your health in color"): copy on
  // the left, auto-scrolling biomarker cards on the right, inside a white card.
  if (gap.marquee?.length) {
    return (
      <section className="bg-neutral-50 py-12 md:py-16">
        <div className={SECTION}>
          <div className="flex flex-col gap-8 rounded-[24px] bg-white p-6 md:flex-row md:p-8">
            {/* Left: copy */}
            <div className="flex flex-shrink-0 flex-col md:w-[38%]">
              <Eyebrow>{gap.eyebrow}</Eyebrow>
              <h2 className="type-title-lg mt-3 text-neutral-900">
                {gap.heading}
              </h2>
              <p className="type-body-md mt-3 text-neutral-500">{gap.body}</p>
            </div>
            {/* Right: auto-scrolling biomarker marquee */}
            <BiomarkerReportMarquee cards={gap.marquee} />
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="py-12 md:py-16">
      <div className={SECTION}>
        <div className="relative isolate flex min-h-[480px] flex-col justify-center overflow-hidden rounded-[28px] p-8 md:min-h-[560px] md:p-14">
          <Image
            src={gap.image.src}
            alt={gap.image.alt}
            fill
            sizes="(max-width: 768px) 100vw, 1200px"
            className="object-cover"
            style={{ objectPosition: gap.image.position ?? "center" }}
          />
          {/* left-darkening scrim so the copy stays legible, image clear on the right */}
          <div
            className="absolute inset-0 bg-gradient-to-r from-neutral-950/85 via-neutral-950/45 to-transparent"
            aria-hidden
          />
          <div className="relative flex max-w-[560px] flex-col gap-5">
            <Eyebrow onDark>{gap.eyebrow}</Eyebrow>
            <h2 className="type-display-sm max-w-[20ch] tracking-tight text-balance text-white">
              {gap.heading}
            </h2>
            <p className="type-body-lg max-w-[52ch] text-pretty text-white/80">
              {gap.body}
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ── 5. Risks — rich card grid (icon chip + body-system badge) ─────────────── */

export function BiomarkerRisksSection({
  config,
}: {
  config: BiomarkerLandingConfig
}) {
  const { risks } = config
  return (
    <section className="bg-neutral-50 py-12 md:py-16">
      <div className={`${SECTION} flex flex-col gap-10`}>
        <div className="mx-auto flex max-w-[680px] flex-col items-center gap-4 text-center">
          <Eyebrow>{risks.eyebrow}</Eyebrow>
          <h2 className="type-display-md tracking-tight text-balance text-neutral-900">
            {risks.heading}
          </h2>
          <p className="type-body-lg text-pretty text-neutral-600">
            {risks.body}
          </p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {risks.items.map((item) => (
            <FeatureCard key={item.title} card={item} />
          ))}
        </div>
      </div>
    </section>
  )
}

/* ── 7. Knowing changes what you do — rich card grid ───────────────────────── */

export function BiomarkerActionSection({
  config,
}: {
  config: BiomarkerLandingConfig
}) {
  const { action } = config
  return (
    <section className="py-12 md:py-16">
      <div className={SECTION}>
        {/* taller image panel; heading at top, icon/texts at the bottom */}
        <div className="relative isolate flex min-h-[680px] flex-col justify-between overflow-hidden rounded-[28px] p-8 md:min-h-[720px] md:p-14">
          <Image
            src={action.image.src}
            alt={action.image.alt}
            fill
            sizes="(max-width: 768px) 100vw, 1200px"
            className="object-cover"
            style={{ objectPosition: action.image.position ?? "center" }}
          />
          {/* reversed scrim — darkest at the bottom behind the icon/texts */}
          <div
            className="absolute inset-0 bg-gradient-to-t from-neutral-950/90 via-neutral-950/45 to-neutral-950/20"
            aria-hidden
          />
          <div className="relative max-w-[640px]">
            <Eyebrow onDark>{action.eyebrow}</Eyebrow>
            <h2 className="type-display-sm mt-3 tracking-tight text-balance text-white">
              {action.heading}
            </h2>
            <p className="type-body-lg mt-4 max-w-[52ch] text-pretty text-white/75">
              {action.body}
            </p>
          </div>
          <div className="relative mt-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {action.items.map((item) => {
              const Icon = item.icon ? ICONS[item.icon] : null
              return (
                <div
                  key={item.title}
                  className="flex flex-col items-start gap-3"
                >
                  {Icon ? <Icon size={28} className="text-white" /> : null}
                  <h3 className="type-title-md tracking-tight text-white">
                    {item.title}
                  </h3>
                  <p className="type-body-md text-pretty text-white/70">
                    {item.body}
                  </p>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}

/* ── 7b. Personas — relatable faces (illustrative profiles, not testimonials) ─ */

export function BiomarkerPersonasSection({
  config,
}: {
  config: BiomarkerLandingConfig
}) {
  const { personas } = config
  return (
    <section className="py-12 md:py-16">
      <div className={`${SECTION} flex flex-col gap-10`}>
        <div className="flex max-w-[640px] flex-col gap-4">
          <Eyebrow>{personas.eyebrow}</Eyebrow>
          <h2 className="type-display-sm tracking-tight text-balance text-neutral-900">
            {personas.heading}
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {personas.items.map((p) => (
            <div
              key={p.name}
              className={`overflow-hidden rounded-[20px] bg-white ${CARD_DEPTH}`}
            >
              <div className="relative aspect-[4/5]">
                <Image
                  src={p.photo.src}
                  alt={p.photo.alt}
                  fill
                  sizes="(max-width: 768px) 100vw, 420px"
                  className="object-cover"
                  style={{ objectPosition: p.photo.position ?? "center" }}
                />
                <div
                  className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/65 to-transparent"
                  aria-hidden
                />
                <h3 className="type-title-md absolute bottom-4 left-5 text-white">
                  {p.name}
                </h3>
              </div>
              <p className="type-body-md p-6 text-pretty text-neutral-500">
                {p.scenario}
              </p>
            </div>
          ))}
        </div>
        <p className="type-body-sm text-neutral-400">{personas.note}</p>
      </div>
    </section>
  )
}

/* ── 8. How it works — image step cards ────────────────────────────────────── */

export function BiomarkerStepsSection({
  config,
}: {
  config: BiomarkerLandingConfig
}) {
  const { steps } = config
  return (
    <section className="py-12 md:py-16">
      <div className={`${SECTION} flex flex-col gap-12`}>
        <div className="flex max-w-[600px] flex-col gap-4">
          <Eyebrow>{steps.eyebrow}</Eyebrow>
          <h2 className="type-display-sm tracking-tight text-balance text-neutral-900">
            {steps.heading}
          </h2>
          <p className="type-body-lg text-pretty text-neutral-600">
            <LinkedCopy
              text={steps.body}
              phrase="Advanced blood test"
              href={config.advancedBloodTestHref}
            />
          </p>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {steps.items.map((item, i) => (
            <div
              key={item.title}
              className="flex flex-col gap-5 rounded-[24px] bg-white p-6"
            >
              {/* image placeholder for now — concentric inner radius */}
              <div className="flex aspect-[4/3] items-center justify-center rounded-[14px] bg-neutral-100 text-neutral-300">
                <ImageSquare size={40} weight="light" />
              </div>
              <div className="flex flex-col gap-3 text-center">
                <p className="type-label-lg text-aubergine-700 tracking-wide uppercase">
                  Step {i + 1}
                </p>
                <h3 className="type-title-lg tracking-tight text-neutral-900">
                  {item.title}
                </h3>
                <p className="type-body-md text-pretty text-neutral-500">
                  {item.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ── 9. FAQ ────────────────────────────────────────────────────────────────── */

export function BiomarkerFaqSection({
  config,
}: {
  config: BiomarkerLandingConfig
}) {
  const { faq } = config
  return (
    <section className="py-12 md:py-16">
      <div
        className={`${SECTION} grid grid-cols-1 gap-10 md:grid-cols-[1fr_2fr]`}
      >
        <h2 className="type-display-sm tracking-tight text-balance text-neutral-900">
          {faq.heading}
        </h2>
        <div className="divide-y divide-neutral-950/[0.07] border-t border-neutral-950/[0.07]">
          {faq.items.map((item) => (
            <details key={item.q} className="group py-5">
              <summary className="type-title-md flex cursor-pointer list-none items-center justify-between gap-4 text-neutral-900">
                {item.q}
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-500 transition-transform group-open:rotate-45">
                  +
                </span>
              </summary>
              <p className="type-body-md mt-3 max-w-[68ch] text-pretty text-neutral-600">
                {item.a}
              </p>
            </details>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ── 10. Closing CTA (brand black) ─────────────────────────────────────────── */

export function BiomarkerClosingSection({
  config,
}: {
  config: BiomarkerLandingConfig
}) {
  const { closing } = config
  return (
    <section className="relative isolate overflow-hidden bg-neutral-950 text-white">
      <Image
        src={closing.image.src}
        alt={closing.image.alt}
        fill
        sizes="100vw"
        className="object-cover"
        style={{ objectPosition: closing.image.position ?? "center" }}
      />
      {/* lighter linear blend so the photo shows through */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-neutral-950/60 via-neutral-950/45 to-neutral-950/65"
        aria-hidden
      />
      <div className={`${SECTION} relative py-16 text-center md:py-24`}>
        <div className="mx-auto flex max-w-[680px] flex-col items-center gap-5">
          <Eyebrow onDark>{closing.eyebrow}</Eyebrow>
          <h2 className="type-display-md max-w-[18ch] tracking-tight text-balance text-white">
            {closing.heading}
          </h2>
          <p className="type-body-lg max-w-[52ch] text-pretty text-white/70">
            {closing.body}
          </p>
          <div className="mt-4">
            <AddonBookingCta location={`go/${config.slug}/closing`}>
              {closing.primaryCta}
            </AddonBookingCta>
          </div>
        </div>
      </div>
    </section>
  )
}
